import {
  CanonicalDataType,
  type MessagePathPart,
} from "@/shared/domain/topics";
import {
  FILTER_CONDITIONS,
  type FilterCondition,
  type FilterStage,
  type StageCombinator,
} from "@/shared/state/visualization/schema/v1/log-panel";

import { columnIdForParts } from "../columnId";

import {
  ALL_COLUMNS_ID,
  ALL_COLUMNS_NAME,
  LOG_TIME_ID,
  LOG_TIME_NAME,
  type ColumnDefinition,
} from "./columnDefinition";

export {
  FILTER_CONDITIONS,
  type FilterCondition,
  type FilterStage,
  type StageCombinator,
};

const validConditions = new Set<string>(FILTER_CONDITIONS);

export interface ColumnFilter {
  stages: FilterStage[];
  combinator: StageCombinator;
}

export interface ColumnFiltersState {
  /**
   * The filters applied to each column.
   *
   * The key is the column's {@link ColumnDefinition.columnId}, and the value is the
   * filter configuration to be applied to that column.
   *
   * See `LogSearchEngine` for how these filters are applied.
   */
  filters: Record<string, ColumnFilter>;
  /**
   * The columns that are allowed to be filtered.
   *
   * The key is the column's {@link ColumnDefinition.columnId}, and the value is the
   * column definition.
   */
  columns: Record<string, ColumnDefinition>;
}

/**
 * Multi-stage filter shape (v2, current).
 */
interface MultiStageFilter {
  stages: FilterStage[];
  combinator?: StageCombinator;
}

function isMultiStageFilter(saved: unknown): saved is MultiStageFilter {
  if (
    typeof saved !== "object" ||
    saved === null ||
    !("stages" in saved) ||
    !Array.isArray((saved as MultiStageFilter).stages)
  ) {
    return false;
  }
  const stages = (saved as MultiStageFilter).stages;
  // Validate that all elements have the expected shape
  for (const stage of stages) {
    if (
      typeof stage !== "object" ||
      stage === null ||
      typeof stage.value !== "string" ||
      typeof stage.condition !== "string" ||
      !validConditions.has(stage.condition)
    ) {
      return false;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// Selectors — pure derivations from ColumnFiltersState
// ---------------------------------------------------------------------------

/**
 * Synthetic column IDs that are not subject to the mutual exclusivity
 * constraint between all-columns and per-column filters.
 */
const SYNTHETIC_COLUMN_IDS = new Set([ALL_COLUMNS_ID, LOG_TIME_ID]);

/**
 * Returns true if the state contains at least one filter for a real
 * per-column entry (i.e., any filter key that isn't a synthetic column).
 * LOG_TIME_ID is excluded because users should be able to filter by time
 * range simultaneously with an all-columns search.
 */
export function hasPerColumnFilters(state: ColumnFiltersState): boolean {
  return Object.keys(state.filters).some(
    (key) => !SYNTHETIC_COLUMN_IDS.has(key),
  );
}

/**
 * Returns true if the state contains an active filter for the synthetic
 * "all columns" entry.
 */
export function hasAllColumnsFilter(state: ColumnFiltersState): boolean {
  return Object.hasOwn(state.filters, ALL_COLUMNS_ID);
}

/**
 * Returns true if adding a filter for `columnId` would violate the mutual
 * exclusivity constraint between all-columns and per-column filters.
 *
 * Synthetic columns (LOG_TIME_ID) are exempt: users can filter by time
 * range alongside an all-columns search.
 */
export function isExclusivityViolation(
  columnId: string,
  state: ColumnFiltersState,
): boolean {
  // Synthetic columns (other than ALL_COLUMNS itself) never conflict.
  if (SYNTHETIC_COLUMN_IDS.has(columnId) && columnId !== ALL_COLUMNS_ID) {
    return false;
  }
  return (
    (columnId === ALL_COLUMNS_ID && hasPerColumnFilters(state)) ||
    (!SYNTHETIC_COLUMN_IDS.has(columnId) && hasAllColumnsFilter(state))
  );
}

/**
 * Rewrite saved `columnFilters` keys from the legacy per-file message-path id to
 * the logical column id ({@link ColumnDefinition.columnId}), now that the same
 * signal read from several files collapses into one column.
 *
 * Resolves ids against `topicData`, which is present synchronously at load — NOT
 * the dynamic column list, which arrives asynchronously and would be empty here,
 * remapping nothing and silently dropping the user's saved filters.
 *
 * Idempotent: keys that aren't known ids (already-migrated names, synthetic
 * {@link ALL_COLUMNS_ID}/{@link LOG_TIME_ID}, or orphaned ids) pass through.
 */
export function migratePersistedColumnFilterKeys<V>(
  savedFilters: Record<string, V> | undefined,
  topicData: readonly {
    messagePath: { id: string; parts: MessagePathPart[] };
  }[],
): Record<string, V> | undefined {
  if (savedFilters === undefined) {
    return undefined;
  }
  const idToColumnId = new Map<string, string>();
  for (const { messagePath } of topicData) {
    idToColumnId.set(messagePath.id, columnIdForParts(messagePath.parts));
  }
  const migrated: Record<string, V> = {};
  for (const [key, value] of Object.entries(savedFilters)) {
    migrated[idToColumnId.get(key) ?? key] = value;
  }
  return migrated;
}

/**
 * Builds the initial column filters state from dynamic columns and saved filters.
 *
 * Handles backward compatibility by normalizing the v1 plain-string filter
 * shape into the current v2 `{ stages: [{ value, condition }] }` shape.
 *
 * - v1 plain string: `"tool"` -> `{ stages: [{ value: "tool", condition: "contains" }] }`
 * - v2 multi-stage object: `{ stages: [...] }` -> preserved as-is
 */
export function initColumnFiltersState(
  dynamicColumns: ColumnDefinition[],
  savedFilters: Record<string, string | MultiStageFilter> | undefined,
): ColumnFiltersState {
  const columns: Record<string, ColumnDefinition> = {
    [ALL_COLUMNS_ID]: { columnId: ALL_COLUMNS_ID, name: ALL_COLUMNS_NAME },
    [LOG_TIME_ID]: {
      columnId: LOG_TIME_ID,
      name: LOG_TIME_NAME,
      dataType: CanonicalDataType.Timestamp,
    },
  };
  for (const column of dynamicColumns) {
    columns[column.columnId] = column;
  }
  // Restore ALL saved filters regardless of whether their column exists yet.
  // Dynamic columns start empty and arrive asynchronously via ColumnsChanged;
  // gating on column existence here would silently discard per-column filters
  // before the real columns are known. The SetColumnsAction handler prunes
  // orphaned filters once the actual column set arrives.
  const filters: Record<string, ColumnFilter> = {};
  for (const [columnId, saved] of Object.entries(savedFilters ?? {})) {
    if (typeof saved === "string") {
      filters[columnId] = {
        stages: [{ value: saved, condition: "contains" }],
        combinator: "or",
      };
    } else if (isMultiStageFilter(saved)) {
      // Current v2 multi-stage shape — preserve stages including value2.
      // Normalize null to undefined for value2 (JSON schema accepts null).
      filters[columnId] = {
        stages: saved.stages.map((s) => {
          const stage: FilterStage = {
            value: s.value,
            condition: s.condition,
          };
          if ("value2" in s && typeof s.value2 === "string") {
            stage.value2 = s.value2;
          }
          return stage;
        }),
        combinator: saved.combinator ?? "or",
      };
    }
  }
  return { filters, columns };
}
